import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import {
  Alert,
  Button,
  HelperText,
  Label,
  Modal,
  ModalActions,
  ModalContent,
  ModalTitleInverse,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TextInput,
  useSnackbar,
} from '@c2fo/liquidity';
import { PercentIcon } from '@c2fo/liquidity/icons';
import Skeleton from '@/enterprise/components/Skeleton';
import { useGlobalPriceDiscoveryOffer } from '@/enterprise/data/useGlobalOffer';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import useDataDog from '@/reporting/DataDog';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import localeRateToDomain from '@/utils/localeRateToDomain';
import useLocaleFormat from '@/utils/useLocaleFormat';
import useRestrictions from '@/utils/useRestrictions';
import { getNormalizedRate, isValidRateInput } from '@/utils/validateRate';
import { OfferLimits, SetOfferFormInputs, validateRate, hasMismatchedRateTypes } from './utils';

interface SetGlobalOfferProps {
  buyerName?: string | null;
  open: boolean;
  onClose: () => void;
  takerMarkets: TakerMarket[];
}

const SetGlobalOfferComponent = ({ open, onClose, takerMarkets }: Omit<SetGlobalOfferProps, 'buyerName'>) => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const showSnackbar = useSnackbar();
  const { addError } = useDataDog();
  const { getRestrictions } = useRestrictions();
  const { canEditOffers } = getRestrictions(takerMarkets);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { mutateAsync: submitGlobalPriceDiscoveryOffer, isPending: submitGlobalPriceDiscoveryOfferLoading } =
    useGlobalPriceDiscoveryOffer(takerMarkets);
  const disableApr = takerMarkets.some((takerMarket) => takerMarket.isDiscountMarket);

  const offerLimits: OfferLimits = {
    disc: {
      minThreshold: 0,
      maxRate: 30,
    },
    apr: {
      minThreshold: 0,
      maxRate: 99,
    },
  };

  const defaultValues = useMemo<SetOfferFormInputs>(
    () => ({
      rate: null,
      rateType: undefined,
      expireOn: null,
    }),
    []
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setError,
    watch,
  } = useForm<SetOfferFormInputs>({ defaultValues });
  const selectedRateType = watch('rateType');
  const mismatchedRateTypes = useMemo(() => {
    return hasMismatchedRateTypes(takerMarkets, selectedRateType);
  }, [selectedRateType, takerMarkets]);

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [defaultValues, open, reset]);

  const onSubmit: SubmitHandler<SetOfferFormInputs> = async (data) => {
    if (!canEditOffers) {
      return;
    }

    const { rate: localeRate, rateType } = data;
    const rate = localeRateToDomain(localeRate);

    if (!rate) {
      return setError(
        'rate',
        {
          message: t('offerSubmitDialog.errors.enterAValidRate'),
        },
        { shouldFocus: true }
      );
    }

    if (!rateType) {
      return setError(
        'rateType',
        {
          message: t('offerSubmitDialog.errors.selectAValidRateType'),
        },
        { shouldFocus: true }
      );
    }

    // validate the rate based on rate type and offer limits
    const validRate = validateRate({ rate, rateType, offerLimits });

    if (!validRate) {
      const numericRate = parseFloat(rate);
      const limits = rateType === 'disc' ? offerLimits.disc : offerLimits.apr;

      let errorMessage: string;
      // if rate is less than or equal to 0, display rate must be greater than 0
      if (numericRate <= limits.minThreshold) {
        errorMessage = t('offerSubmitDialog.errors.enterRateGreaterThanZero');
      } else if (numericRate > limits.maxRate) {
        // if rate is greater than the max rate, display rate cannot be greater than the max rate
        errorMessage =
          rateType === 'disc'
            ? t('offerSubmitDialog.errors.discRateCannotBeGreaterThanMax', { maxRate: limits.maxRate })
            : t('offerSubmitDialog.errors.aprRateCannotBeGreaterThanMax', { maxRate: limits.maxRate });
      } else {
        // if rate is not valid, display enter a valid rate
        errorMessage = t('offerSubmitDialog.errors.enterAValidRate');
      }

      return setError(
        'rate',
        {
          message: errorMessage,
        },
        { shouldFocus: true }
      );
    }

    setSubmitError(null);

    const normalizedRate = getNormalizedRate(rate);
    const isDiscountBidding = rateType === 'disc';

    const submitValues = {
      isDiscountBidding,
      ...(!isDiscountBidding && { maxApr: normalizedRate }),
      ...(isDiscountBidding && { maxDiscount: normalizedRate }),
    };

    try {
      await submitGlobalPriceDiscoveryOffer(submitValues, {
        onSuccess: () => {
          onClose();
          showSnackbar({
            message: t('globalOfferSubmitDialog.offerUpdate'),
          });
          trackEnterpriseEvent('global-offer::submitted', { rateType });
        },
        onError: (error) => {
          setSubmitError(t('globalOfferSubmitDialog.offerUpdateError'));
          addError('Global Offer Submit Error', {
            error,
            location: 'SetGlobalOffer -> onSubmit',
            takerMarkets: takerMarkets.map((tm) => ({
              makerOrganizationUuid: tm.makerOrganizationUuid,
              marketUuid: tm.marketUuid,
              supplierDivisionUuid: tm.takerDivisionId,
            })),
          });
        },
      });
    } catch (error) {
      setSubmitError(t('globalOfferSubmitDialog.offerUpdateError'));
      addError('Global Offer Submit Error', {
        error,
        location: 'SetGlobalOffer -> onSubmit',
        takerMarkets: takerMarkets.map((tm) => ({
          makerOrganizationUuid: tm.makerOrganizationUuid,
          marketUuid: tm.marketUuid,
          supplierDivisionUuid: tm.takerDivisionId,
        })),
      });
    }
  };

  const trackRateChange = (rateType: SetOfferFormInputs['rateType']) => {
    trackEnterpriseEvent('global-offer-rate-type::changed', { rateType });
  };

  return (
    <>
      <ModalContent className="space-y-4">
        <div className="space-y-6">
          <div className="rounded-md bg-canvas p-4">
            <form className="grid grid-cols-1 gap-4 sm:grid-cols-2" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-1">
                <Label htmlFor="rate">{t('core.offerRate')}</Label>
                <Controller
                  control={control}
                  name="rate"
                  render={({ field }) => (
                    <TextInput
                      id="rate"
                      className="w-full"
                      endIcon={PercentIcon}
                      hasError={!!errors.rate}
                      placeholder={t('taker.setARate')}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        if (isValidRateInput(e.target.value)) {
                          field.onChange(e.target.value);
                        }
                      }}
                    />
                  )}
                />
                {errors.rate && errors.rate.message && <HelperText variant="error">{errors.rate.message}</HelperText>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="rateType">{t('core.rateType')}</Label>
                <Controller
                  control={control}
                  name="rateType"
                  render={({ field }) => (
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value);
                        trackRateChange(value as SetOfferFormInputs['rateType']);
                      }}
                      value={field.value}
                    >
                      <SelectTrigger aria-label={t('core.rateType')} hasError={!!errors.rateType}>
                        <SelectValue placeholder={t('offer.selectRateType')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="disc">
                          <p>{t('core.discount')}</p>
                          <p className="block text-wrap text-sm [button_&]:hidden">{t('offer.flatRateMessage')}</p>
                        </SelectItem>
                        {!disableApr && (
                          <SelectItem value="apr">
                            <p>{t('core.apr')}</p>
                            <p className="block text-wrap text-sm [button_&]:hidden">{t('offer.recurringMessage')}</p>
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.rateType && errors.rateType.message && (
                  <HelperText variant="error">{errors.rateType.message}</HelperText>
                )}
              </div>
            </form>
            {selectedRateType && mismatchedRateTypes && (
              <div className="mt-4">
                <Alert
                  type="warning"
                  variant="outlined"
                  description={
                    selectedRateType === 'disc'
                      ? t('globalOfferSubmitDialog.rateTypeMismatchWarningDiscount')
                      : t('globalOfferSubmitDialog.rateTypeMismatchWarningApr')
                  }
                  full
                />
              </div>
            )}
          </div>
          <div>
            <ul className="ml-4 list-disc space-y-2">
              <li>{t('offer.applyToAllMarketsMessage')}</li>
              <li>{t('allOfferDialogs.description2')}</li>
              <li>{t('allOfferDialogs.description3')}</li>
              <li>{t('offerSubmitDialog.disclaimer')}</li>
            </ul>
          </div>
          {submitError && <Alert type="error" variant="filled" description={submitError} full />}
        </div>
      </ModalContent>
      <ModalActions>
        <Button variant="secondary" onClick={onClose}>
          {t('core.cancel')}
        </Button>
        <Button
          variant="cta"
          onClick={handleSubmit(onSubmit)}
          className="sm:min-w-[100px]"
          disabled={submitGlobalPriceDiscoveryOfferLoading}
          loading={submitGlobalPriceDiscoveryOfferLoading}
        >
          {t('core.submit')}
        </Button>
      </ModalActions>
    </>
  );
};

const SetGlobalOffer = (props: SetGlobalOfferProps) => {
  const { t } = useTranslation();
  const { asNumber } = useLocaleFormat();

  return (
    <Modal open={props.open} onClose={props.onClose} className="sm:w-[750px]">
      <ModalTitleInverse>
        <span className="flex flex-col">
          <span>{t('core.setOfferLabel')}</span>
          {props.buyerName && <span className="text-sm font-normal text-text-secondary">{props.buyerName}</span>}
          <span className="text-sm font-normal text-text-secondary">
            {t('taker.globalOfferMsg', {
              count: props.takerMarkets.length ?? '',
              formattedCount: asNumber({ value: props.takerMarkets.length ?? '' }),
            })}
          </span>
        </span>
      </ModalTitleInverse>
      <QueryBoundaries
        LoadingComponent={() => (
          <div className="m-6 space-y-4 rounded-md bg-canvas p-4">
            <div className="space-y-2">
              <Skeleton className="h-6 w-1/3 opacity-70" shadow={false} />
              <Skeleton className="h-10 w-full opacity-60" shadow={false} />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-6 w-1/4 opacity-70" shadow={false} />
              <Skeleton className="h-10 w-full opacity-60" shadow={false} />
            </div>
            <div className="space-y-2 pt-2">
              <Skeleton className="h-6 w-1/2 opacity-70" shadow={false} />
              <Skeleton className="h-24 w-full opacity-60" shadow={false} />
            </div>
          </div>
        )}
      >
        <SetGlobalOfferComponent open={props.open} onClose={props.onClose} takerMarkets={props.takerMarkets} />
      </QueryBoundaries>
    </Modal>
  );
};

export default SetGlobalOffer;
